import { useMemo, type DependencyList } from "react";

/**
 * A memoized function cache that stores results based on arguments.
 * The cache resets whenever the dependency list (`deps`) changes.
 *
 * @template T - The function type whose result should be cached.
 * @param callback - The function whose results should be cached.
 * @param deps - Dependency list that triggers cache reset when changed.
 * @returns A memoized function with argument-based caching.
 *
 * @example
 * ```ts
 * const cachedFormatter = useCache((value: number) => {
 *   console.log("Computing...");
 *   return value.toFixed(2);
 * }, []);
 *
 * console.log(cachedFormatter(42)); // "Computing..." -> "42.00"
 * console.log(cachedFormatter(42)); // Cached result -> "42.00"
 * ```
 */
export function useCache<T extends (...args: any[]) => any>(
  callback: T,
  deps: DependencyList,
) {
  return useMemo<T & { cache: Map<string, ReturnType<T>> }>(() => {
    const cache = new Map<string, ReturnType<T>>();

    const func = (...args: Parameters<T>) => {
      const key = JSON.stringify(args);
      if (cache.has(key)) {
        return cache.get(key);
      }
      const result = callback(...args);
      cache.set(key, result);
      return result;
    };

    Object.assign(func, { cache });

    return func as any;
  }, deps);
}
